import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palmtree, LogOut, Icon, FileText, Users, Building2, Menu, X, RefreshCw, Settings } from 'lucide-react';
import { cowHead } from '@lucide/lab';
import { usePermissions } from '@/hooks/usePermissions';
import { useInitialRoute } from '@/components/ProtectedRoute';
import { clearAllCache } from '@/utils/cacheUtils';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TopLevelLayout({ user, onLogout, children, activeTab = 'agricultura' }) {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(activeTab);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { hasPermission, hasAnyPermission, isAdmin } = usePermissions(user);
  const initialRoute = useInitialRoute(user);

  // Verificar si tiene permisos para agricultura (cualquier permiso de agricultura)
  const hasAgriculturaAccess = isAdmin || hasAnyPermission([
    'agricultura.rrhh.trabajadores.view',
    'agricultura.rrhh.expediente.view',
    'agricultura.rrhh.asistencia.view',
    'agricultura.rrhh.disciplinario.view',
    'agricultura.produccion.hoja_pesador.view',
    'agricultura.produccion.lotes.view',
    'agricultura.produccion.registros.view',
    'agricultura.produccion.reportes.view',
    'agricultura.produccion.salud_seguridad.view',
    'agricultura.produccion.cronograma.view',
    'agricultura.maquinaria.inventario.view',
    'agricultura.maquinaria.inspecciones.view',
    'agricultura.maquinaria.uso.view',
    'agricultura.maquinaria.mantenimiento.view',
    'agricultura.admin.empresas.view',
    'agricultura.admin.fincas.view',
    'agricultura.admin.usuarios.view',
    'agricultura.admin.historial.view',
    'agricultura.admin.tareas.view',
    'agricultura.admin.notificaciones.view'
  ]);
  
  // Verificar si tiene permisos para ganadería (cualquier permiso de ganadería)
  const hasGanaderiaAccess = isAdmin || hasAnyPermission([
    'ganaderia.inventario.animales.view',
    'ganaderia.peso.registros.view',
    'ganaderia.salud.tratamientos.view',
    'ganaderia.salud.diagnosticos.view',
    'ganaderia.reproduccion.embarazos.view',
    'ganaderia.reproduccion.genealogia.view',
    'ganaderia.dashboard.view'
  ]);

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    if (tab === 'agricultura') {
      // Usar initialRoute para ir a la primera página permitida del usuario
      handleNavigate(initialRoute);
    } else if (tab === 'ganaderia') {
      handleNavigate('/ganaderia');
    }
  };

  const handleForceSync = async () => {
    try {
      toast.info('Limpiando caché y sincronizando...', { duration: 2000 });
      const success = await clearAllCache();
      
      if (success) {
        toast.success('Caché limpiado. Recargando página...', { duration: 1500 });
        setTimeout(() => {
          window.location.reload(true);
        }, 1500);
      } else {
        toast.warning('No se pudo limpiar todo el caché. Recargando...', { duration: 1500 });
        setTimeout(() => {
          window.location.reload(true);
        }, 1500);
      }
    } catch (error) {
      console.error('Error al sincronizar:', error);
      toast.error('Error al sincronizar. Recargando página...', { duration: 1500 });
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Global */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Menú Hamburguesa (Solo Móvil) */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <div className="flex items-center">
              <img src="/logo.png" alt="CDP Logo" className="h-10 sm:h-12 w-auto" />
            </div>

            {/* Tabs Principales: Agricultura y Ganadería (Solo Desktop) */}
            <div className="hidden md:flex flex-1 justify-center">
              <Tabs value={currentTab} onValueChange={handleTabChange} className="w-auto">
                <TabsList className="bg-gray-100">
                  {hasAgriculturaAccess && (
                    <TabsTrigger 
                      value="agricultura" 
                      className="flex items-center px-6 py-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                    >
                      <Palmtree className="w-5 h-5 mr-2" />
                      Agricultura
                    </TabsTrigger>
                  )}
                  {hasGanaderiaAccess && (
                    <TabsTrigger 
                      value="ganaderia" 
                      className="flex items-center px-6 py-2 data-[state=active]:bg-rose-600 data-[state=active]:text-white"
                    >
                      <Icon iconNode={cowHead} className="w-5 h-5 mr-2" />
                      Ganadería
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>

            {/* User Menu - OCULTO EN MÓVIL */}
            <div className="hidden md:flex items-center space-x-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 rounded-full px-2 py-1 hover:bg-transparent focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 active:bg-transparent">
                    {user.profile_picture ? (
                      <img
                        src={`${BACKEND_URL}${user.profile_picture}`}
                        alt={user.username}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shadow-md border-2 border-emerald-200"
                      />
                    ) : (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-md">
                        <span className="text-xs sm:text-sm font-bold text-white">
                          {user.full_name ? user.full_name.split(' ').slice(0, 2).map(n => n[0]).join('') : user.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="text-left hidden lg:block">
                      <p className="text-sm font-medium text-gray-900">{user.full_name || user.username}</p>
                      <p className="text-xs text-gray-500">{user.username}</p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="text-sm font-medium">{user.full_name || user.username}</p>
                      <p className="text-xs text-gray-500">{user.username}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(isAdmin || hasPermission('agricultura.admin.notificaciones.view')) && (
                    <DropdownMenuItem onClick={() => navigate('/bandeja')} className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                      Bandeja de Entrada
                    </DropdownMenuItem>
                  )}
                  {(isAdmin || hasPermission('agricultura.admin.usuarios.view')) && (
                    <DropdownMenuItem onClick={() => navigate('/usuarios')} className="cursor-pointer">
                      <Users className="w-4 h-4 mr-2" />
                      Gestión de Usuarios
                    </DropdownMenuItem>
                  )}
                  {(isAdmin || hasPermission('agricultura.admin.historial.view')) && (
                    <DropdownMenuItem onClick={() => navigate('/historial')} className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                    Historial de Cambios
                    </DropdownMenuItem>
                  )}
                  {(isAdmin || hasPermission('agricultura.admin.empresas.view')) && (
                    <DropdownMenuItem onClick={() => navigate('/empresas')} className="cursor-pointer">
                      <Building2 className="w-4 h-4 mr-2" />
                      Empresa
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/configuracion')} className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Configuración
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-72 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4">
          {/* Header Sidebar */}
          <div className="flex items-center justify-between mb-6">
            <img src="/logo.png" alt="CDP Logo" className="h-10 w-auto" />
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center space-x-3 mb-6 pb-6 border-b border-gray-200">
            {user.profile_picture ? (
              <img
                src={`${BACKEND_URL}${user.profile_picture}`}
                alt={user.username}
                className="w-12 h-12 rounded-full object-cover shadow-md border-2 border-emerald-200"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-md">
                <span className="text-sm font-bold text-white">
                  {user.full_name ? user.full_name.split(' ').slice(0, 2).map(n => n[0]).join('') : user.username.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-900">{user.full_name || user.username}</p>
              <p className="text-xs text-gray-500">{user.username}</p>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="space-y-2">
            {/* Módulos Principales */}
            {hasAgriculturaAccess && (
              <button
                onClick={() => handleTabChange('agricultura')}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                  currentTab === 'agricultura' 
                    ? 'bg-emerald-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Palmtree className="w-5 h-5 mr-3" />
                <span className="font-medium">Agricultura</span>
              </button>
            )}
            {hasGanaderiaAccess && (
              <button
                onClick={() => handleTabChange('ganaderia')}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                  currentTab === 'ganaderia' 
                    ? 'bg-rose-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon iconNode={cowHead} className="w-5 h-5 mr-3" />
                <span className="font-medium">Ganadería</span>
              </button>
            )}

            {/* Divisor */}
            <div className="border-t border-gray-200 my-4" />

            {/* Admin Options */}
            {(isAdmin || hasPermission('agricultura.admin.notificaciones.view')) && (
              <button
                onClick={() => handleNavigate('/bandeja')}
                className="w-full flex items-center px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <FileText className="w-5 h-5 mr-3" />
                <span>Bandeja de Entrada</span>
              </button>
            )}
            {(isAdmin || hasPermission('agricultura.admin.usuarios.view')) && (
              <button
                onClick={() => handleNavigate('/usuarios')}
                className="w-full flex items-center px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Users className="w-5 h-5 mr-3" />
                <span>Gestión de Usuarios</span>
              </button>
            )}
            {(isAdmin || hasPermission('agricultura.admin.historial.view')) && (
              <button
                onClick={() => handleNavigate('/historial')}
                className="w-full flex items-center px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <FileText className="w-5 h-5 mr-3" />
                <span>Historial de Cambios</span>
              </button>
            )}
            {(isAdmin || hasPermission('agricultura.admin.empresas.view')) && (
              <button
                onClick={() => handleNavigate('/empresas')}
                className="w-full flex items-center px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Building2 className="w-5 h-5 mr-3" />
                <span>Empresa</span>
              </button>
            )}

            {/* Divisor */}
            <div className="border-t border-gray-200 my-4" />

            {/* Botón de Sincronización */}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleForceSync();
              }}
              className="w-full flex items-center px-4 py-3 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5 mr-3" />
              <span className="font-medium">Sincronizar Datos</span>
            </button>

            {/* Logout */}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onLogout();
              }}
              className="w-full flex items-center px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3" />
              <span className="font-medium">Cerrar Sesión</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Contenido */}
      <main>
        {children}
      </main>
    </div>
  );
}
